import * as d3 from 'd3';

import { SvgManager } from '../SvgManager';
import { ILogger, LoggerFactory, getId } from '../../helpers/helpers';
import { KpiWithOptions } from './Kpi';
import { DisplayKpi } from './DisplayKpi';
import { toDisplayKpi, light, dark } from './BulletChartHelper';
import { Config, getConfig } from '../helpers/rectangleChartHelper';

export class MultiBulletChart {
    private _dark = dark;
    private _dispatch: d3.Dispatch;
    private _group: d3.Selection<any>;
    private _height: number;
    private _light = light;
    private _logger: ILogger;
    private _margin = 20;
    private _whiteSpace = 0.75;
    private _width: number;

    constructor(private _svgManager: SvgManager) {
        this._group = _svgManager.container();
        this._logger = LoggerFactory.getLogger('WideTech.Charting.MultiBulletChart', false, 0);
        const size = _svgManager.getSize();
        this.width(size.width)
            .height(size.height);
        _svgManager.addResizeFunction((w, h) => {
            this.resize(w, h);
        });
        this._dispatch = d3.dispatch('mouseenter', 'mouseleave');
    }

    private resize(width: number, height: number) {
        this.width(width)
            .height(height);
        this._logger.debug('Resize');
        this.render(this._group.selectAll('g.bullet-chart'));
    }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (arguments.length) {
            this._height = value;
            return this;
        }
        return this._height;
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (arguments.length) {
            this._width = value;
            return this;
        }
        return this._width;
    }

    private _localRatio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._localRatio = value;
            return this;
        }
        return this._localRatio;
    }

    on(event: string, callback: (d, i) => void): this {
        this._dispatch.on(event, callback);
        return this;
    }

    update(data: KpiWithOptions[]): void {
        this._logger.debug('Update');
        const ratio = this.ratio() || 1;

        const plotWidth = this.width() - this._margin * ratio * 2;
        const dataBound = this._group
            .selectAll('.bullet-chart')
            .data(data.map(d => toDisplayKpi(d, plotWidth)));
        dataBound
            .exit()
            .remove();
        const enterSelection = dataBound
            .enter()
            .append('g')
            .classed('bullet-chart', true);
        this.enter(enterSelection);

        const nameGroup = this._group.selectAll('.name-group');

        nameGroup
            .selectAll('.compilable')
            .remove();

        nameGroup
            .append('g')
            .classed('compilable', true)
            .append('text')
            .classed('name', true)
            .style('clip-path', (d, i) => {
                let url = '';
                try {
                    url = d3.select(nameGroup[0][i]).select('clipPath').attr('id');
                } catch (e) { }

                return `url("#${url}")`;
            });

        this.render(dataBound);
    }

    private enter(enterSelection: d3.Selection<DisplayKpi>) {
        const self = this;
        const container = enterSelection
            .append('g')
            .classed('container', true)
            .attr('transform', `translate(${this._margin},5)`)
            .style('pointer-events', 'all')
            .on('mouseenter', function (d) {
                self._dispatch.mouseenter(this, d);
            })
            .on('mouseleave', function (d) {
                self._dispatch.mouseleave(this, d);
            });
        const heading = container.append('g')
            .classed('heading', true)
            .attr('transform', `translate(${0},${10})`);
        const nameGroup = heading.append('g')
            .classed('name-group', true);
        const ids = this.appendClipPath(nameGroup, 'name-clippath');
        nameGroup
            .append('g')
            .classed('compilable', true)
            .append('text')
            .classed('name', true)
            .style('clip-path', (d, i) => `url("#${ids[i]}")`);
        const options = heading.append('g')
            .classed('options', true);
        const plot = container.append('g')
            .classed('chart', true)
            .attr('transform', `translate(${0},${20})`);

        const chartIds = this.appendClipPath(plot, 'chart-clippath');
        const rectHeight = 20;
        const background = plot.append('g')
            .classed('rectangles', true)
            .style('clip-path', `url("#${chartIds[0]}")`);
        background.append('rect')
            .classed('background', true)
            .style('fill', this._dark)
            .attr('height', rectHeight);
        background.append('rect')
            .classed('range', true)
            .style('fill', this._light)
            .attr('height', rectHeight);
        background.append('rect')
            .classed('background', true)
            .style('fill', this._dark)
            .attr('height', rectHeight);
        plot.append('g')
            .classed('axis-group', true)
            .classed('axis', true)
            .classed('horizontal-axis', true)
            .attr('transform', (d, i) => `translate(${0},${rectHeight})`);
        const kpi = plot.append('g')
            .classed('kpi', true)
            .style('clip-path', `url("#${chartIds[0]}")`);
        kpi.append('rect')
            .classed('marker', true);
        kpi.append('rect')
            .classed('target', true)
            .attr('width', 2);
    }

    private appendClipPath(selection: d3.Selection<any>, classed: string, prefix: string = 'clipPath'): string[] {
        const ids = [];
        selection.append('defs')
            .append('clipPath')
            .attr('id', () => {
                const id = getId(prefix);
                ids.push(id);
                return id;
            })
            .append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .classed(classed, true)
            .classed('clip-path-rectangle', true);
        return ids;
    }

    private render(selection: d3.Selection<DisplayKpi>) {
        this._logger.debug('Selection length: ' + selection.length);
        if (selection.length) {
            const ratio = this.ratio() || 1;
            const count = selection[0].length;
            this._logger.debug('Data count: ' + count);
            const config = getConfig(count, this.width(), this.height(), this._margin, ratio);
            selection
                .transition()
                .attr('transform', (d, i) => `translate(${0},${i * (config.height)})`);

            selection.select('g.container')
                .attr('transform', `translate(${this._margin * ratio},${5 * ratio})`);
            selection
                .select('.compilable')
                .on('click', (d, i) => {
                    if (!!d.onClick) {
                        d.onClick(d.originalObject, i);
                    }
                })
                .each(function (d) {
                    if (d.attributes) {
                        d3.select(this).attr(d.attributes);
                    }
                });
            selection.selectAll('.range')
                .style('fill', d => !d.options || d.options.isRangeVisible ? this._light : this._dark);
            selection
                .classed('xs', config.xs)
                .classed('sm', config.sm);
            this._svgManager.setHeight(count * config.height);
            this.updateOptions(selection, config.plotWidth, config.boxWidth, config.xs);

            const headingTranslate = `translate(${0},${config.headingHeight / 2})`;
            selection
                .select('.heading')
                .transition()
                .attr('transform', headingTranslate)
                .selectAll('.clip-path-rectangle')
                .attr('y', -config.headingHeight * 3 / 4)
                .attr('height', config.headingHeight);
            this.updateNames(selection, config);
            selection.select('.chart')
                .select('.chart-clippath')
                .attr('height', config.rectHeight)
                .attr('width', Math.max(config.plotWidth, 0));
            selection.select('.chart')
                .transition()
                .attr('transform', `translate(${0},${config.headingHeight})`);
                
            this.updateRectangles(selection, config);
            this.updateAxes(selection, config, ratio);
            this.updateTarget(selection, config.rectHeight);
            this.updateMarker(selection, config.rectHeight);
        }
    }

    private updateRectangles(selection: d3.Selection<DisplayKpi>, config: Config) {
        selection
            .select('.rectangles')
            .selectAll('rect')
            .data((d: DisplayKpi) => {
                const scale = d.scale.range([0, config.plotWidth]);
                d.axis.ticks(config.ticksCount);
                return d.ranges.map((r, i) => {
                    return {
                        min: scale(r.min),
                        max: scale(r.max),
                        color: i === 1 ? this._light : (d.backColor ? d.backColor : this._dark)
                    };
                });
            })
            .transition()
            .attr('height', config.rectHeight)
            .attr('x', (d, i) => Math.max(d.min, 0))
            .attr('width', (d, i) => Math.max(d.max - d.min, 0))
            .style('fill', d => d.color);
    }

    private updateAxes(selection: d3.Selection<DisplayKpi>, config: Config, ratio: number) {
        const axisSelection = selection.select('.axis-group');
        axisSelection
            .each(function (d, i) {
                const thisSelection = d3.select(this);
                thisSelection.call(d.axis);
                thisSelection.select('.domain')
                    .style('stroke', d.axisColor)
                    .style('stroke-width', d.axisColor ? '2px' : null);
                const ticks = thisSelection.selectAll('.tick');
                ticks.select('text')
                    .attr('y', (3 + config.tickHeight) / ratio)
                    .classed('tickLabel', true)
                    .style('fill', d.axisColor);
                ticks.select('line')
                    .style('stroke', d.axisColor)
                    .attr('y2', config.tickHeight / ratio);
            })
            .transition()
            .attr('transform', `translate(${0},${config.rectHeight})`);
    }

    private updateNames(selection: d3.Selection<DisplayKpi>, config: Config) {
        const nameGroup = selection
            .select('.name-group');
        nameGroup
            .select('.name-clippath')
            .attr('width', d => Math.max(0, config.plotWidth - d.options.length * config.boxWidth));

        nameGroup
            .select('text')
            .text(d => d.formattedValue)
            .classed('clickable', d => !!d.onClick);
    }

    private updateOptions(selection: d3.Selection<DisplayKpi>, plotWidth: number, boxWidth: number, xs: boolean) {
        this._logger.debug('Update options');
        const ratio = this._localRatio || 1;
        const optionBound = selection.select('.options')
            .selectAll('.option')
            .data(d => d.options);
        optionBound.exit().remove();
        const enterOption = optionBound.enter()
            .append('g')
            .classed('option', true);
        const ids = this.appendClipPath(enterOption, 'option-clippath');

        enterOption.append('text')
            .style('text-anchor', (d: any) => 'end')
            .style('clip-path', (d, i) => `url('#${ids[i]}')`);
        enterOption.append('path');

        optionBound.attr('transform', (d, i) => `translate(${plotWidth - (i) * boxWidth},${0})`);
        optionBound.select('.option-clippath')
            .attr('x', -boxWidth * .9)
            .attr('width', Math.max(boxWidth * .9, 0));
        optionBound.select('text')
            .attr('x', (d: any) => d.symbolPath ? -15 * ratio : null)
            .text((d: any) => d.value);
        optionBound.select('path')
            .attr('transform', `scale(${(xs ? 0.5 : 0.7) * ratio}) translate(${-10 * ratio},${-4 * ratio})`)
            .attr('d', (d: any) => d.symbolPath ? d.symbolPath() : null);
    }

    private updateMarker(selection: d3.Selection<DisplayKpi>, rectHeight: number): d3.Selection<DisplayKpi> {
        selection.select('rect.marker')
            .transition()
            .attr('width', d => Math.max(d.scale(d.value), 0))
            .attr('y', rectHeight * (this._whiteSpace / 2))
            .attr('height', rectHeight * (1 - this._whiteSpace))
            .style('fill', d => d.color);
        return selection;
    }

    private updateTarget(selection: d3.Selection<DisplayKpi>, rectHeight: number): d3.Selection<DisplayKpi> {
        selection.select('rect.target')
            .style('visibility', d => d.target || d.target === 0 ? 'visible' : 'hidden')
            .transition()
            .attr('x', d => d.target || d.target === 0 ? d.scale(d.target) - 1 : 0)
            .attr('width', Math.max(rectHeight / 10, 0))
            .attr('y', rectHeight * (this._whiteSpace / 4))
            .attr('height', rectHeight * (1 - this._whiteSpace / 2));
        return selection;
    }
}